
import { useCallback, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

import { FilterState, ReportQueryResponse } from "./types";

export const useReportQuery = (
  searchTerm: string,
  filterState: FilterState,
  toast: any
) => {
  const { selectedCategory, sortBy, sortOrder, currentPage, itemsPerPage, isHovering } = filterState;

  // Create query key that includes all filter parameters
  const queryKey = useMemo(() => 
    ['reports', searchTerm, selectedCategory, sortBy, sortOrder, currentPage, itemsPerPage], 
    [searchTerm, selectedCategory, sortBy, sortOrder, currentPage, itemsPerPage]
  );

  // Fetcher (pas de debounce ici : la clé de requête change déjà avec les filtres,
  // et un debounce lodash renvoie undefined au premier appel).
  const fetchReports = useCallback(async (): Promise<ReportQueryResponse> => {
    const query = (supabase.from as any)('reports')
      .select('*', { count: 'exact' });

    if (selectedCategory) {
      query.eq('category', selectedCategory);
    }

    if (searchTerm) {
      query.or(`title.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%,location.ilike.%${searchTerm}%`);
    }

    query.order(sortBy === 'date' ? 'created_at' : sortBy, { ascending: sortOrder === 'asc' });

    if (!isHovering) {
      const start = (currentPage - 1) * itemsPerPage;
      query.range(start, start + itemsPerPage - 1);
    }

    const { data, error, count } = await query;

    if (error) {
      throw error;
    }

    return { data: data || [], count: count ?? 0 };
  }, [searchTerm, selectedCategory, sortBy, sortOrder, currentPage, itemsPerPage, isHovering]);


  // Fetch the reports with proper typing
  const queryResult = useQuery<ReportQueryResponse>({
    queryKey,
    queryFn: fetchReports,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  });

  // Process the reports for filtering and display
  const filteredReports = useMemo(() => {
    if (!queryResult.data?.data) return [];
    return queryResult.data.data;
  }, [queryResult.data]);

  // Calculate total pages
  const totalPages = useMemo(() => {
    if (!queryResult.data?.count) return 1;
    return Math.ceil(queryResult.data.count / itemsPerPage);
  }, [queryResult.data, itemsPerPage]);

  return {
    queryResult,
    filteredReports,
    totalPages
  };
};
